
// ABOUTME: Component for displaying a list of referrals with detailed information
// ABOUTME: Shows referral status, candidate details, and provides a clean table view

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Mail, Phone, User, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

import SecureResumeDownload from './SecureResumeDownload';

interface Referral {
  id: string;
  candidate_name: string;
  candidate_email: string;
  candidate_phone?: string;
  candidate_linkedin?: string;
  candidate_resume_url?: string;
  relationship: string;
  notes?: string;
  status: string;
  submitted_at: string;
  referrer_email: string;
  job_roles: {
    title: string;
    department: string;
    bonus_amount: string;
    created_by?: string;
  };
  score?: number;
}

interface ReferralListProps {
  referrals: Referral[];
  roleTitle?: string;
}

const ReferralList = ({ referrals, roleTitle }: ReferralListProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hired':
        return 'default';
      case 'interview_scheduled':
      case 'interviewed':
        return 'secondary';
      case 'under_review':
        return 'outline';
      case 'rejected':
      case 'withdrawn':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const formatStatus = (status: string) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (referrals.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Referrals Found</h3>
          <p className="text-gray-600">
            {roleTitle ? `There are no referrals for "${roleTitle}" yet.` : 'No referrals found for this role.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <User className="w-5 h-5" />
          <span>Referrals {roleTitle && `for ${roleTitle}`}</span>
          <Badge variant="outline" className="ml-2">
            {referrals.length} total
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Candidate</TableHead>
              <TableHead>Contact Info</TableHead>
              <TableHead>Relationship</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Score</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {referrals.map((referral) => (
              <TableRow key={referral.id}>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{referral.candidate_name}</div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Mail className="w-3 h-3" />
                      <span className="truncate max-w-[200px]">{referral.candidate_email}</span>
                    </div>
                    {referral.candidate_linkedin && (
                      <div className="flex items-center space-x-2 text-sm text-blue-600">
                        <ExternalLink className="w-3 h-3" />
                        <a 
                          href={referral.candidate_linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline truncate max-w-[200px]"
                        >
                          LinkedIn
                        </a>
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    {referral.candidate_phone && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Phone className="w-3 h-3" />
                        <span>{referral.candidate_phone}</span>
                      </div>
                     )}
                     <SecureResumeDownload
                       resumeUrl={referral.candidate_resume_url || ''}
                       candidateName={referral.candidate_name}
                       roleCreatedBy={referral.job_roles.created_by}
                     />
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    <div className="font-medium">{referral.relationship}</div>
                    {referral.notes && (
                      <div className="text-gray-600 text-xs mt-1 max-w-[150px] truncate" title={referral.notes}>
                        {referral.notes}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getStatusColor(referral.status)}>
                    {formatStatus(referral.status)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Calendar className="w-3 h-3" />
                    <span>{format(new Date(referral.submitted_at), 'MMM dd, yyyy')}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {typeof referral.score === 'number' ? (
                    <span className="font-medium">{Math.round(Math.max(0, Math.min(100, referral.score)))}</span>
                  ) : (
                    <span className="text-muted-foreground">N/A</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default ReferralList;
